import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import "./Dashboard.css";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip, Legend, 
} from 'chart.js';
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useContext } from "react";
import { AuthContext } from "../components/AuthContext";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
ChartJS.register(ChartDataLabels);

const Dashboard = () => {

  // Variáveis de estado
  const { user, token, logout } = useContext(AuthContext);
  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("Todas");
  const [selectedProperty, setSelectedProperty] = useState("Todas");
  const [realtimeUsers, setRealtimeUsers] = useState({});
  const [consolidatedRealtimeUsers, setConsolidatedRealtimeUsers] = useState(0);
  const [topPagesRealtime, setTopPagesRealtime] = useState([]);
  const [consolidatedTopPagesRealtime, setConsolidatedTopPagesRealtime] = useState([]);
  const [traffic, setTraffic] = useState({});
  const [consolidatedTraffic, setConsolidatedTraffic] = useState([]);
  const [topPages, setTopPages] = useState({});
  const [consolidatedTopPages, setConsolidatedTopPages] = useState([]);
  const navigate = useNavigate()

  // Obtenção dos dados das contas GA via Backend

  useEffect(() => {    
    if (!user) {
      // Redireciona para login se não estiver autenticado
      navigate("/login");
    }
    if (!user || !token) return;
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        const email = user;
        setUserEmail(email);
        const accountsRes = await axios.get(`/api/ga/accounts_ga?email=${email}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAccounts(accountsRes.data);   
        setAccountsLoaded(true);  
      } catch (err) {
        console.error("Erro ao buscar dados iniciais:", err);
      } finally {
      }
    };
    fetchAccounts();    
  }, [navigate, user, token]);

  // useEffect que busca dados após as contas serem definidas
  useEffect(() => {
    const email = user;
    if (accountsLoaded && accounts.length > 0 && token && email) {
      fetchData(email, token, true);
    }
  }, [accounts, accountsLoaded, token, user]);

  // Obtenção dos dados dos gráficos via backend 

  const fetchData = async (email, token, showLoading = false) => {
    try {

      if (showLoading) setLoading(true);

      const res = await axios.get(`/api/ga/data_ga?email=${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const {
        consolidatedRealtimeUsers,
        realtimeUsers,
        consolidatedRealtimeTopPages,
        realtimeTopPages,
        traffic,
        consolidatedTraffic,
        topPages,
        consolidatedTopPages,
      } = res.data;

     
      setConsolidatedRealtimeUsers(consolidatedRealtimeUsers || 0);
      setRealtimeUsers(realtimeUsers || {});
      setConsolidatedTopPagesRealtime(consolidatedRealtimeTopPages || []);
      setTopPagesRealtime(realtimeTopPages || {});
      setTraffic(traffic || {});
      setConsolidatedTraffic(consolidatedTraffic || []);
      setTopPages(topPages || {});
      setConsolidatedTopPages(consolidatedTopPages || []);

    } catch (err) {
      console.error("Erro ao buscar dados do GA:", err);
    } finally {
      if (showLoading) setLoading(false);
    }
  };


 
    // Recarregamento automático da tela

  useEffect(() => {
    if (!user || !token) return;

    const interval = setInterval(() => {      
      if (user) {
        fetchData(user, token, false);
      }
    }, 30000); // 30 segundos
  
    return () => clearInterval(interval); // limpa o intervalo ao desmontar
  }, [user, token]);

  
  // Filtragem ActivesUsers (Reatime)

  const filteredData = useMemo(() => {
    if (selectedAccount === "Todas" && selectedProperty === "Todas") {
      return consolidatedRealtimeUsers;
    }
    const filtered = [];
    if (selectedAccount === "Todas") {
      for (const acc in realtimeUsers) {
        for (const prop in realtimeUsers[acc]) {
          filtered.push({ account: acc, property: prop, users: realtimeUsers[acc][prop] });
        }
      }
    } else if (selectedProperty === "Todas") {
      for (const prop in realtimeUsers[selectedAccount] || {}) {
        filtered.push({ account: selectedAccount, property: prop, users: realtimeUsers[selectedAccount][prop] });
      }
    } else {
      filtered.push({
        account: selectedAccount,
        property: selectedProperty,
        users: realtimeUsers[selectedAccount]?.[selectedProperty] || 0,
      });
    }
    return filtered;
  }, [selectedAccount, selectedProperty, consolidatedRealtimeUsers, realtimeUsers]);



  // Filtragem TopFivePages (Realtime)

  const filteredTopPagesRealtime = useMemo(() => {
    if (selectedAccount === "Todas" && selectedProperty === "Todas") {
      return consolidatedTopPagesRealtime;
    }
  
    const accountData = topPagesRealtime[selectedAccount] || {};
    const propertyData = accountData[selectedProperty] || [];
  
    return propertyData;

  }, [selectedAccount, selectedProperty, consolidatedTopPagesRealtime, topPagesRealtime]);

  

  // Filtragem ActivesUsers (Period)
  
  const filteredTraffic = useMemo(() => {
    if (selectedAccount === "Todas" && selectedProperty === "Todas") {
      return consolidatedTraffic.map((value, index) => ({
        period: ["Hoje", "Ultimos 7 dias", "Ultimos 30 dias", "Últimos 6 meses"][index],
        activeUsers: value,
      }));
    }
  
    const accountData = traffic[selectedAccount] || {};
    const propertyData = accountData[selectedProperty] || [];
  
    return propertyData;
  }, [selectedAccount, selectedProperty, traffic, consolidatedTraffic]);


  
  // Filtragem TopFivePages (Period) 

  const filteredTopPages = useMemo(() => {
    if (selectedAccount === "Todas" && selectedProperty === "Todas") {
      return consolidatedTopPages;
    }
  
    const accountData = topPages[selectedAccount] || {};
    const propertyData = accountData[selectedProperty] || [];
  
    return propertyData;
  }, [selectedAccount, selectedProperty, consolidatedTopPages, topPages]); 



  // Gera as opções dos filtros de conta GA e propriedade/site 

  const accountOptionsSet = new Set();
  Object.keys(realtimeUsers || {}).forEach(acc => accountOptionsSet.add(acc));
  Object.keys(topPagesRealtime || {}).forEach(acc => accountOptionsSet.add(acc));
  Object.keys(topPages || {}).forEach(acc => accountOptionsSet.add(acc));
  Object.keys(traffic || {}).forEach(acc => accountOptionsSet.add(acc));
  const accountOptions = Array.from(accountOptionsSet);
  
  const propertyOptionsSet = new Set();
  Object.keys(realtimeUsers[selectedAccount] || {}).forEach(acc => propertyOptionsSet.add(acc));
  Object.keys(topPagesRealtime[selectedAccount] || {}).forEach(acc => propertyOptionsSet.add(acc));
  Object.keys(topPages[selectedAccount] || {}).forEach(acc => propertyOptionsSet.add(acc));
  Object.keys(traffic[selectedAccount] || {}).forEach(acc => propertyOptionsSet.add(acc));
  const propertyOptions = selectedAccount !== "Todas"
    ? Array.from(propertyOptionsSet)
    : [];  

  // Plugin para o gráfico TopFivePage (Realtime)

  const customLabelsPlugin = {
    id: "customLabels",
    afterDraw(chart) {
      const {
        ctx,
        chartArea: { top },
        scales: { y },
      } = chart;
    
      ctx.save();
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#111";
    
      y.ticks.forEach((tick, i) => {
        const label = y.getLabelForValue(tick.value);
        const [account, page] = label.split("\n");
        const yPos = y.getPixelForTick(i);

        const xPos = y.left - 140;
    
        ctx.font = "bold 13px sans-serif";
        ctx.fillText(account, xPos, yPos - 8);
    
        ctx.font = "12px sans-serif";
        ctx.fillText(page, xPos, yPos + 8);
      });
    
      ctx.restore();
    },
  };  

  // Truncador das labels

  const truncate = (str, maxLength = 40) => {
    if (!str) return "";
    return str.length > maxLength ? str.slice(0, maxLength - 3) + "..." : str;
  };    

  const handleAccountChange = (e) => {
    const selected = e.target.value;
    setSelectedAccount(selected);
    if (selected === "Todas") {
      setSelectedProperty("Todas"); // Reseta o filtro de propriedade também
    }
  };

  // Renderização dos gráficos   
  
  return (
    <div className="dashboard-wrapper">
      <div> <span className="username">{userEmail}</span></div>
      <div className="dashboard-header">
        <h1>Gestão GA</h1>
        <div className="dashboard-user">
        
        
        <div className="dashboard-filters">
            <div className="filter-group">
              <label></label>
              <select value={selectedAccount} onChange={handleAccountChange}>
                <option value="Todas">Contas GA</option>
                {[...accountOptions].sort().map((acc) => (
                  <option key={acc} value={acc}>{acc}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label></label>
              {selectedAccount !== "Todas" && (
                <select value={selectedProperty} onChange={(e) => setSelectedProperty(e.target.value)}>
                  <option value="Todas">Propriedades (Site/Blog)</option>
                  {[...propertyOptions].sort().map((prop) => (
                    <option key={prop} value={prop}>{prop}</option>
                  ))}
                </select>
              )}
            </div>



            <div>
              <button className="filter-group" onClick={logout}>Sair</button>
            </div>
            
          </div>         
          
        </div>
      </div>
      

      {loading ? (

        <div className="loading-spinner-dashboard">
          <ClipLoader color="#007bff" size={20} />
          <span>Aguarde, carregando dados...</span>
        </div>

      ) : (
        <div className="dashboard-grid">
          <div className="card" style={{ textAlign: "center", padding: "10px 20px 20px 20px" }}>
            <h2 style={{ fontSize: "20px", marginBottom: "10px" }}>
              Usuários Ativos<br /> (tempo real)
            </h2>
            
            {/* Número de usuários ativos */}
            {(() => {
              const totalUsers = Array.isArray(filteredData)
                ? filteredData.reduce((total, item) => total + (item.users || 0), 0)
                : (filteredData?.users ?? filteredData ?? 0);

              return (
                <>
                  <div style={{ fontSize: "68px", fontWeight: "bold", color: "#2566DD", height: "80%", padding: "17% 0 0 0" }}>
                    {totalUsers}
                    <div style={{ fontSize: "28px", color: "#555", marginTop: "5px" }}>
                    {totalUsers === 1 ? "Usuário" : "Usuários"}
                  </div>
                  </div>
                </>
              );
            })()}
            {/* Nome dos sites/blogs ou "Todos os Sites/Blogs" */}
            <div style={{ marginTop: "10px", fontSize: "16px", color: "#555" }}>
              {Array.isArray(filteredData)
                ? (filteredData.length > 1
                    ? "Todos os Sites/Blogs"
                    : `${filteredData[0]?.account || "Site/Blog"}`)
                : "Todos os Sites/Blogs"}
            </div>
          </div>
          <div className="card">
            <h2>Top Páginas/Posts<br /> (tempo real)</h2>
            <Bar
              data={{
                labels: filteredTopPagesRealtime.map((p) => {
                  const account = truncate(p.accountName || selectedAccount || "Conta", 20); 
                  const page = truncate(p.pagePath || "", 23);            
                  return `${account}\n${page}`;
                }),
                datasets: [
                  {
                    label: "Páginas/Posts (Em tempo real)",
                    data: filteredTopPagesRealtime.map((p) => p.views),
                    backgroundColor: "rgba(37, 102, 221, 0.86)",
                    borderRadius: 2,
                    //barThickness: 30,
                    maxBarThickness: 40,
                    categoryPercentage: 0.7, // controla o espaço entre barras (menor = mais apertado)
                    barPercentage: 1.0, 
                  },
                ],
              }}
              options={{
                responsive: true,
                indexAxis: "y",
                plugins: {
                  legend: { display: false },
                  datalabels: {
                    align: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.x.max || 1;
                      const percent = value / max;
                      const isMobile = window.innerWidth <= 768;
                      if (isMobile) {
                        // No mobile, seja mais conservador para evitar texto cortado
                        return percent < 0.35 ? "end" : "start";
                      } else {
                        return percent < 0.15 ? "end" : "start";
                      }
                    },
                    anchor: "end",
                    color: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.x.max || 1;
                      const percent = value / max;
                      const isMobile = window.innerWidth <= 768;
                      if (isMobile) {
                        return percent < 0.35 ? "#6c757d" : "#fff";
                      } else {
                        return percent < 0.15 ? "#6c757d" : "#fff";
                      }
                    },
                    formatter: Math.round,
                    font: {
                      weight: "normal",
                      size: 14,
                    },
                    clamp: true,
                    clip: false,
                  },
                  tooltip: {
                    enabled: true,
                    callbacks: {
                      label: (context) => `${context.parsed.x} visualizações`,
                    },
                  },
                },
                layout: {
                  padding: {
                    left: 150, 
                    right: 10,
                    top: 10,
                    bottom: 10,
                  },
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    grid: { display: false },
                    ticks: { precision: 0 },
                  },
                  y: {
                    ticks: {
                      display: false, // Esconde o padrão
                    },
                    grid: {
                      display: false,
                      drawTicks: false,
                    },
                  },
                },
              }}
              height={300}
              plugins={[ChartDataLabels, customLabelsPlugin]}
            />
          </div>

          <div className="card wide">
            <h2>Top Páginas/Posts<br /> (mês atual)</h2>
            <Bar
              data={{
                labels: filteredTopPages.map((p) => {
                  const account = truncate(p.accountName || selectedAccount || "Conta", 20); 
                  const page = truncate(p.pagePath || "", 23);            
                  return `${account}\n${page}`;
                }),
                datasets: [
                  {
                    label: "Páginas/Posts (Em tempo real)",
                    data: filteredTopPages.map((p) => p.views),
                    backgroundColor: "rgba(37, 102, 221, 0.86)",
                    borderRadius: 2,
                    //barThickness: 30,
                    maxBarThickness: 40,
                    categoryPercentage: 0.7, // controla o espaço entre barras (menor = mais apertado)
                    barPercentage: 1.0, 
                  },
                ],
              }}
              options={{
                responsive: true,
                indexAxis: "y",
                plugins: {
                  legend: { display: false },
                  datalabels: {
                    align: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.x.max || 1;
                      const percent = value / max;
                      const isMobile = window.innerWidth <= 768;
                      if (isMobile) {
                        // No mobile, seja mais conservador para evitar texto cortado
                        return percent < 0.35 ? "end" : "start";
                      } else {
                        return percent < 0.2 ? "end" : "start";
                      }
                    },
                    anchor: "end",
                    color: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.x.max || 1;
                      const percent = value / max;
                      const isMobile = window.innerWidth <= 768;
                      if (isMobile) {
                        return percent < 0.35 ? "#6c757d" : "#fff";
                      } else {
                        return percent < 0.2 ? "#6c757d" : "#fff";
                      }
                    },
                    formatter: Math.round,
                    font: {
                      weight: "normal",
                      size: 14,
                    },
                    clamp: true,
                    clip: false,
                  },
                  tooltip: {
                    enabled: true,
                    callbacks: {
                      label: (context) => `${context.parsed.x} visualizações`,
                    },
                  },
                },
                layout: {
                  padding: {
                    left: 150, 
                    right: 10,
                    top: 10,
                    bottom: 10,
                  },
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    grid: { display: false },
                    ticks: { precision: 0 },
                  },
                  y: {
                    ticks: {
                      display: false, // Esconde o padrão
                    },
                    grid: {
                      display: false,
                      drawTicks: false,
                    },
                  },
                },
              }}
              height={300}
              plugins={[ChartDataLabels, customLabelsPlugin]}
            />
          </div>

          <div className="card wide" style={{ marginBottom: "10%" }}>
            <h2>Usuários Ativos<br /> (período)</h2>
            <Bar
              data={{
                labels: filteredTraffic.map((item) => item.period),
                datasets: [
                  {
                    label: "",
                    data: filteredTraffic.map((item) => item.activeUsers),
                    backgroundColor: 'rgba(37, 102, 221, 0.86',
                    borderRadius: 6,
                  },
                ],
              }}
              options={{ responsive: true,
                plugins: {
                  legend: { display: false },
                  datalabels: {
                    align: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.y.max || 1; // ou x.max se for horizontal
                      const percent = value / max;
                      return percent < 0.1 ? 'end' : 'center'; 
                      // Se for muito pequeno (<10% do máximo), coloca fora ('end'), senão dentro ('center')
                    },
                    anchor: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.y.max || 1;
                      const percent = value / max;
                      return percent < 0.1 ? 'end' : 'center'; 
                    },
                    color: (context) => {
                      const value = context.dataset.data[context.dataIndex];
                      const max = context.chart.scales.y.max || 1;
                      const percent = value / max;
                      return percent < 0.1 ? '#6c757d' : '#fff'; 
                      // Se o valor for pequeno, deixa o rótulo cinza escuro; se for grande, branco
                    },
                    font: {
                      weight: 'normal',
                      size: 14,
                    },
                    clamp: true, // impede que o texto ultrapasse o gráfico
                    clip: false,
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.raw} usuários`,
                    },
                  },
                },
                layout: {
                  padding: {
                    left: 0, 
                    right: 0,
                    top: 30,
                    bottom: 10,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      display: false,   // Tira as linhas horizontais
                    },
                    ticks: { stepSize: 1 },
                  },
                  x: {
                    grid: {
                      display: false,   // Tira as linhas verticais
                    },   
                  },               
                }, }}
                height={300}
                plugins={[ChartDataLabels]}
            />
          </div>          
        </div>
      )}
    </div>
  );
};

export default Dashboard;
